import React, { useState } from 'react';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// ── Helpers (mirrors DocumentDNAList) ─────────────────────────────────────

const getCommunityColor = (id) => {
  if (!id) return '#6366f1';
  let hash = 0;
  for (let i = 0; i < id.length; i++) hash = id.charCodeAt(i) + ((hash << 5) - hash);
  return `hsl(${Math.abs(hash) % 360}, 60%, 50%)`;
};

const getRatingLabel = (rating) => {
  if (rating >= 0.7) return { label: 'High',   cls: 'rating-high' };
  if (rating >= 0.4) return { label: 'Medium', cls: 'rating-med' };
  return { label: 'Low', cls: 'rating-low' };
};

const STATUS_CONFIG = {
  processed:  { label: 'Processed',  cls: 'dna-status-ok',      icon: 'fas fa-check-circle'        },
  completed:  { label: 'Processed',  cls: 'dna-status-ok',      icon: 'fas fa-check-circle'        },
  indexed:    { label: 'Processed',  cls: 'dna-status-ok',      icon: 'fas fa-check-circle'        },
  pending:    { label: 'Pending',    cls: 'dna-status-pending',  icon: 'fas fa-clock'               },
  queued:     { label: 'Pending',    cls: 'dna-status-pending',  icon: 'fas fa-clock'               },
  processing: { label: 'Processing', cls: 'dna-status-pending',  icon: 'fas fa-clock'               },
  failed:     { label: 'Failed',     cls: 'dna-status-fail',     icon: 'fas fa-exclamation-circle'  },
  error:      { label: 'Failed',     cls: 'dna-status-fail',     icon: 'fas fa-exclamation-circle'  },
};

const getStatus = (s) => {
  const key = (s || '').toLowerCase();
  return STATUS_CONFIG[key] || { label: s || 'Unknown', cls: 'dna-status-none', icon: 'fas fa-minus-circle' };
};

const extractFileName = (filePath) => {
  if (!filePath) return null;
  const parts = filePath.replace(/\\/g, '/').split('/');
  return parts[parts.length - 1] || filePath;
};

const isLocalFile = (fileName) => {
  if (!fileName) return false;
  return !fileName.startsWith('http://') && !fileName.startsWith('https://');
};

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric',
    });
  } catch { return iso; }
};

// ── DNAStrip (same as DocumentDNAList, for consistency) ────────────────────

const DNAStrip = ({ communities }) => {
  if (!communities || communities.length === 0) {
    return <div className="dna-strip dna-strip-empty"><span>No community data</span></div>;
  }
  const total = communities.reduce((sum, c) => sum + (c.community_rating ?? 0), 0) || 1;
  return (
    <div className="dna-strip" title="Community distribution">
      {communities.map((c, i) => (
        <div
          key={c.community_id ?? i}
          className="dna-strip-segment"
          style={{
            width: `${((c.community_rating ?? 0) / total) * 100}%`,
            background: getCommunityColor(c.community_id),
            minWidth: 4,
          }}
          title={c.community_title || c.community_id}
        />
      ))}
    </div>
  );
};

// ── Community Card ─────────────────────────────────────────────────────────

const CommunityCard = ({ community, totalWeight, onSelect }) => {
  const color    = getCommunityColor(community.community_id);
  const weight   = community.community_rating ?? 0;
  const pct      = totalWeight > 0 ? ((weight / totalWeight) * 100).toFixed(1) : '0.0';
  const barPct   = totalWeight > 0 ? ((weight / totalWeight) * 100) : 0;
  const { label: ratingLabel, cls: ratingCls } = getRatingLabel(weight);
  const title    = community.community_title || community.community_id || 'Community';
  // community_distribution items may carry any of these summary fields
  const summary  = community.community_summary
    || community.summary
    || community.description
    || null;

  return (
    <div
      className="ddd-comm-card"
      onClick={() => onSelect(community)}
      style={{ borderTop: `4px solid ${color}` }}
    >
      {/* ── Header: title + rating badge ── */}
      <div className="ddd-comm-card-header">
        <h3 className="ddd-comm-card-title">{title}</h3>
        <span className={`community-rating-badge ${ratingCls}`}>{ratingLabel}</span>
      </div>

      {/* ── Rating bar ── */}
      <div className="community-rating-bar-wrap">
        <div
          className="community-rating-bar-fill"
          style={{ width: `${barPct}%`, background: color }}
        />
      </div>

      {/* ── Weight percentage ── */}
      <div className="ddd-comm-weight-row">
        <span className="ddd-comm-weight-label">Knowledge weight</span>
        <span className="ddd-comm-weight-value" style={{ color }}>{pct}%</span>
      </div>

      {/* ── Summary (if available) ── */}
      {summary && (
        <p className="ddd-comm-summary">{summary}</p>
      )}

      {/* ── Footer ── */}
      <div className="ddd-comm-card-footer">
        <span className="ddd-comm-explore-hint">
          Explore <i className="fas fa-arrow-right"></i>
        </span>
      </div>
    </div>
  );
};

// ── Main DocumentDNADetail ─────────────────────────────────────────────────

const DocumentDNADetail = ({ doc, onBack, onSelectCommunity }) => {
  const [downloading, setDownloading] = useState(false);

  if (!doc) return null;

  const status = getStatus(doc.processing_status);
  const fileName = extractFileName(doc.file_name) || doc.url || `Document #${doc.pdf_id ?? doc.id}`;
  const isLocal = isLocalFile(doc.file_name);
  const communities = doc.community_distribution || [];
  const totalWeight = communities.reduce((sum, c) => sum + (c.community_rating ?? 0), 0);

  // ── Download handler (mirrors BotResultsTable exactly) ──────────────────
  const handleDownload = async (e) => {
    e.preventDefault();
    if (!isLocal) {
      // External URL — just open in new tab
      window.open(doc.url || doc.file_name, '_blank', 'noreferrer');
      return;
    }
    const docId = doc.pdf_id ?? doc.id;
    if (!docId) {
      alert('Cannot download: document ID not found.');
      return;
    }
    setDownloading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/files/${docId}/download`, {
        credentials: 'include',
      });
      const result = await response.json();
      if (result.success && result.url) {
        const link = document.createElement('a');
        link.href = result.url;
        link.setAttribute('download', result.filename || '');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        alert(result.error || 'Failed to generate download link.');
      }
    } catch (err) {
      console.error('Download error:', err);
      alert('Network error. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  const handleCommunitySelect = (community) => {
    if (onSelectCommunity) {
      onSelectCommunity({
        id: community.community_id,
        title: community.community_title,
      });
    }
  };

  return (
    <div className="ddd-container">

      {/* ── Back & Title row ── */}
      <div className="ddd-header-row">
        <button className="cdp-back-btn" onClick={onBack}>
          <i className="fas fa-arrow-left"></i> Back to Document DNA
        </button>
      </div>

      {/* ── Document Hero card ── */}
      <div className="ddd-hero">
        <div className="ddd-hero-icon-wrap">
          <i className={`${isLocal ? 'fas fa-file-pdf' : 'fas fa-globe'} ddd-hero-icon`}></i>
        </div>
        <div className="ddd-hero-body">
          <h2 className="ddd-hero-title" title={doc.file_name || doc.url}>
            {fileName}
          </h2>
          <div className="ddd-hero-meta">
            {doc.provider && (
              <span className="ddd-meta-chip ddd-chip-provider">
                <i className="fas fa-building"></i> {doc.provider}
              </span>
            )}
            <span className={`ddd-meta-chip ${status.cls}`}>
              <i className={status.icon}></i> {status.label}
            </span>
            {doc.created_date && (
              <span className="ddd-meta-chip ddd-chip-date">
                <i className="fas fa-calendar-alt"></i> {formatDate(doc.created_date)}
              </span>
            )}
            {doc.last_processed_at && (
              <span className="ddd-meta-chip ddd-chip-date">
                <i className="fas fa-clock"></i> Processed {formatDate(doc.last_processed_at)}
              </span>
            )}
          </div>
          {doc.url && !isLocal && (
            <a
              className="ddd-url-link"
              href={doc.url}
              target="_blank"
              rel="noopener noreferrer"
              title={doc.url}
            >
              <i className="fas fa-external-link-alt"></i> {doc.url.length > 80 ? doc.url.slice(0, 77) + '…' : doc.url}
            </a>
          )}
          {(doc.description || doc.summary) && (
            <p className="ddd-hero-desc">{doc.description || doc.summary}</p>
          )}
        </div>
        <div className="ddd-hero-actions">
          <button
            className="btn-primary ddd-download-btn"
            onClick={handleDownload}
            disabled={downloading}
            title={isLocal ? 'Download this file' : 'Open document in new tab'}
          >
            {downloading
              ? <><i className="fas fa-spinner fa-spin"></i> Downloading…</>
              : isLocal
                ? <><i className="fas fa-download"></i> Download</>
                : <><i className="fas fa-external-link-alt"></i> Open Document</>
            }
          </button>
        </div>
      </div>

      {/* ── Stats bar ── */}
      <div className="ddd-stats-bar">
        <div className="ddd-stat">
          <span className="ddd-stat-value">{doc.total_edges_contributed ?? 0}</span>
          <span className="ddd-stat-label">Relationships</span>
        </div>
        <div className="ddd-stat">
          <span className="ddd-stat-value">{communities.length}</span>
          <span className="ddd-stat-label">Communities</span>
        </div>
        <div className="ddd-stat">
          <span className="ddd-stat-value">
            <span className={`ddd-status-dot ${status.cls}`}></span>
            {status.label}
          </span>
          <span className="ddd-stat-label">Processing Status</span>
        </div>
        <div className="ddd-stat ddd-stat-strip">
          <DNAStrip communities={communities} />
          <span className="ddd-stat-label">Knowledge DNA</span>
        </div>
      </div>

      {/* ── Communities section ── */}
      <div className="ddd-section">
        <div className="ddd-section-header">
          <h3 className="ddd-section-title">
            <i className="fas fa-diagram-project"></i> Connected Communities
          </h3>
          <p className="ddd-section-subtitle">
            Click a community to explore its entities and relationships.
          </p>
        </div>

        {communities.length === 0 ? (
          <div className="ddd-empty-communities">
            <i className="fas fa-circle-xmark"></i>
            <p>No community connections found for this document.</p>
            <span>This may indicate the document has not yet been processed by the knowledge graph engine.</span>
          </div>
        ) : (
          <div className="ddd-comm-grid">
            {communities
              .slice()
              .sort((a, b) => (b.community_rating ?? 0) - (a.community_rating ?? 0))
              .map((c, i) => (
                <CommunityCard
                  key={c.community_id ?? i}
                  community={c}
                  totalWeight={totalWeight}
                  onSelect={handleCommunitySelect}
                />
              ))
            }
          </div>
        )}
      </div>

      {/* ── Journey hint ── */}
      <div className="ddd-journey-hint">
        <i className="fas fa-route"></i>
        <span>
          <strong>Document → DNA → Community → Entity</strong>
          &nbsp;— Click any community card above to drill into its entities, then open the entity context window for full intelligence.
        </span>
      </div>

    </div>
  );
};

export default DocumentDNADetail;
